import * as React from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { GetProperty, UpdatePropertyLocation } from '../../REST/properties';
import { loadModules, loadCss } from 'esri-loader';
import {GetMapLayers, AddUpdateMapLayer} from '../../REST/maplayer';
import { createCombinedControlForEsriLoader } from '../../Components/Gis';
import Access from '../../Utils/authorize';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});



export default function MapSelectView({ text = null, refresh }) {
    const [open, setOpen] = React.useState(false);
    const { id } = useParams();

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);

        if (typeof refresh === 'function') {
            refresh();
        }
    };

    return (
        <div>
            <Button variant="outlined" onClick={handleClickOpen}>
                {Array.isArray(text) && text.length > 0 ? text.join(',') : 'Select Location'}
            </Button>

            <Dialog
                fullScreen
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
            >
                <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>

                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                Select Location
                            </Typography>
                        </Toolbar>
                    </AppBar>

                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        {open && (
                            <PropertyLocationMap
                                propertyId={id}
                                
                            />
                        )}
                    </Box>
                </Box>
            </Dialog>
        </div>
    );
}

function PropertyLocationMap({ propertyId }) {
    const mapDivRef = React.useRef(null);
    const viewRef = React.useRef(null);

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        let isMounted = true;
        let clickHandle = null;

        async function initMap() {
            try {
                setLoading(true);
                setError(null);

                if (!propertyId) {
                    throw new Error('Missing propertyId from route params.');
                }

                loadCss('https://js.arcgis.com/4.22/esri/themes/light/main.css');

                console.log('Loading ArcGIS modules...');

                const [
                    esriConfig,
                    Map,
                    MapView,
                    Graphic,
                    locator,Search, BasemapToggle
                    
                ] = await loadModules(
                    [
                        'esri/config',
                        'esri/Map',
                        'esri/views/MapView',
                        'esri/Graphic',
                        'esri/rest/locator', 'esri/widgets/Search', 'esri/widgets/BasemapToggle'
                    ],
                    {
                        url: 'https://js.arcgis.com/4.22/'
                    }
                );

                console.log('ArcGIS modules loaded.');

                if (!isMounted || !mapDivRef.current) {
                    return;
                }

                const apiKey =  process.env.REACT_APP_ESRI_KEY;

                if (apiKey) {
                    esriConfig.apiKey = apiKey;
                } else {
                    console.warn('REACT_APP_ARCGIS_API_KEY is missing. ArcGIS basemap/geocoder may not work.');
                }

                const map = new Map({
                    basemap: 'arcgis-navigation'
                });

                const view = new MapView({
                    container: mapDivRef.current,
                    map,
                    center: [-119.734, 39.193],
                    zoom: 16
                });
                view.ui.add(new Search({ view: view }), 'bottom-right');
                view.ui.add(new BasemapToggle({ view: view, nextBasemap: 'satellite' }), 'bottom-left');

                view.ui.remove('zoom');
                await createCombinedControlForEsriLoader({
                    view: view,
                    canEditConfig:  Access.isAdmin('Settings'),
                    collapsedByDefault: false,
                    loadConfig: async () => {
                        const raw = await GetMapLayers(); // or localStorage/db
                        return raw ? raw.data : [];
                    },
                    saveConfig: async (configs) => {
                        const result = await AddUpdateMapLayer(configs); // async persistence
                        return result.data;
                    },
                    defaultLayerConfigs: [],
                });
                viewRef.current = view;

                const addGraphics = (items) => {
                    view.graphics.removeAll();

                    if (!Array.isArray(items)) {
                        return;
                    }

                    items.forEach((item, idx) => {
                        if (!item || !item.location || !item.location.coordinates) {
                            return;
                        }

                        const { propertyName, location, _id } = item;
                        const { coordinates } = location;

                        const pointGraphic = new Graphic({
                            geometry: {
                                type: 'point',
                                latitude: coordinates[0],
                                longitude: coordinates[1]
                            },
                            attributes: {
                                OBJECTID: idx,
                                name: propertyName,
                                id: _id
                            },
                            symbol: {
                                type: 'web-style',
                                name: 'box-pin',
                                styleName: 'Esri2DPointSymbolsStyle'
                            },
                            popupTemplate: {
                                title: '{name}',
                                content: '{name}'
                            }
                        });

                        view.graphics.add(pointGraphic);
                    });
                };

                const recenter = (data) => {
                    let center = [-119.73776383440293, 39.191158471149436];

                    if (
                        Array.isArray(data) &&
                        data.length > 0 &&
                        data[0].location &&
                        Array.isArray(data[0].location.coordinates)
                    ) {
                        center = [
                            data[0].location.coordinates[1],
                            data[0].location.coordinates[0]
                        ];
                    }

                    view.goTo({
                        center,
                        zoom: 12
                    }).catch(() => { });
                };

                const saveLocation = async (mapPoint) => {
                    const currentView = viewRef.current;

                    if (!currentView || currentView.destroyed) {
                        return;
                    }

                    const longitude = Math.round(mapPoint.longitude * 100000) / 100000;
                    const latitude = Math.round(mapPoint.latitude * 100000) / 100000;

                    const payload = {
                        type: 'Point',
                        coordinates: [latitude, longitude]
                    };

                    const saveResponse = await UpdatePropertyLocation(propertyId, payload);

                    if (!isMounted || !viewRef.current || viewRef.current.destroyed) {
                        return;
                    }

                    // Replace only the marker/pointer.
                    addGraphics([saveResponse.data]);

                    // Keep the map extent exactly as-is.
                    // Do not call recenter().
                    // Do not call refresh().
                    // Do not recreate the map.

                    if (viewRef.current.popup) {
                        viewRef.current.popup.close();
                    }
                };

                const showAddress = (address, mapPoint) => {
                    const root = document.createElement('div');

                    const addressNode = document.createElement('p');
                    addressNode.textContent = address || 'No address found.';

                    const button = document.createElement('button');
                    button.type = 'button';
                    button.textContent = 'Save Location';
                    button.style.cursor = 'pointer';
                    button.style.padding = '6px 12px';

                    button.addEventListener('click', () => {
                        button.disabled = true;
                        button.textContent = 'Saving...';

                        saveLocation(mapPoint).catch((ex) => {
                            console.error(ex);
                            setError('Unable to save selected location.');

                            button.disabled = false;
                            button.textContent = 'Save Location';
                        });
                    });

                    root.appendChild(addressNode);
                    root.appendChild(button);

                    const longitude = Math.round(mapPoint.longitude * 100000) / 100000;
                    const latitude = Math.round(mapPoint.latitude * 100000) / 100000;

                    view.popup.open({
                        title: `${longitude}, ${latitude}`,
                        content: root,
                        location: mapPoint
                    });
                };

                const serviceUrl = 'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer';

                await view.when();

                console.log('MapView ready.');

                if (isMounted) {
                    setLoading(false);
                }

                // Load property AFTER map is visible
                try {
                    console.log('Loading property...', propertyId);

                    const propertyResponse = await GetProperty(propertyId);

                    console.log('Property response:', propertyResponse);

                    const propertyData = propertyResponse.data || propertyResponse;

                    addGraphics([propertyData]);
                    recenter([propertyData]);
                } catch (propertyEx) {
                    console.error('Property load failed:', propertyEx);
                    setError('Map loaded, but property location could not be loaded.');
                }

                clickHandle = view.on('click', async (event) => {
                    try {
                        const response = await locator.locationToAddress(serviceUrl, {
                            location: event.mapPoint
                        });

                        showAddress(response.address, event.mapPoint);
                    } catch (ex) {
                        showAddress('No address found.', event.mapPoint);
                    }
                });
            } catch (ex) {
                console.error('Map load failed:', ex);

                if (isMounted) {
                    setError(ex.message || 'Unable to load map.');
                    setLoading(false);
                }
            }
        }
        initMap();

        return () => {
            isMounted = false;

            if (clickHandle) {
                clickHandle.remove();
                clickHandle = null;
            }

            if (viewRef.current) {
                viewRef.current.destroy();
                viewRef.current = null;
            }
        };
    }, [propertyId]);

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        zIndex: 10,
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.7)'
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box
                    sx={{
                        position: 'absolute',
                        zIndex: 11,
                        top: 16,
                        left: 16,
                        right: 16,
                        p: 2,
                        bgcolor: 'error.main',
                        color: 'error.contrastText',
                        borderRadius: 1
                    }}
                >
                    {error}
                </Box>
            )}

            <div
                ref={mapDivRef}
                style={{
                    width: '100%',
                    height: '100%'
                }}
            />
        </Box>
    );
}
